import BaseListPresenter from "../../base/BaseListPresenter";

class EmpResignationPagePresenter extends BaseListPresenter {
  constructor(
    view,
    findObjectUseCase,
    countObjectUseCase,
    upsertUseCase,
    updateSchemaUseCase,
    getObjectUseCase
  ) {
    super(view, findObjectUseCase, countObjectUseCase, getObjectUseCase);
    this.upsertUseCase = upsertUseCase;
    this.updateSchemaUseCase = updateSchemaUseCase;
    this.getObjectUseCase = getObjectUseCase;
    this.lastFetchTime = 0;
    this.lastDataHash = "";
    this.lastStatusHash = ""; // Track status changes separately
  }

  componentDidMount() {
    this.loadResignations();
    this.getEmployeeDetails();
  }

  async getEmployeeDetails() {
    const user = this.view.getCurrentUser();

    const id = user?.employee?.id;
    try {
      this.view.showLoading();

      const object = await this.getObjectUseCase.execute("employees", id);

      this.view.setState({ userDetails: object });
      this.view.hideLoading();
      return object;
    } catch (error) {
      console.error("Failed to fetch employee details: ", error);
    }
  }

  loadResignations() {
    this.init();
    this.sort = { createdAt: -1 }; // Newest first
    this.fetchResignations();
  }

  onClickPrint() {
    this.view.exportPDF();
  }

  async findUsersEmployee(id) {
    const query = {
      where: { id: id },
      include: ["all", "employee"],
    };

    try {
      const [user] = await this.findObjectUseCase.execute("users", query);
      return user.employee;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async submitResignation(formData) {
    this.view.setSubmitting(true);

    try {
      const { resignationType, effectiveDate, resignationLetter } = formData;
      if (!resignationType || !effectiveDate) {
        throw new Error(
          "Please fill in all required fields and attach your resignation letter"
        );
      }

      const currentUser = this.view.getCurrentUser();
      if (!currentUser?.employee?.id) {
        throw new Error("Employee information not available");
      }

      const user = await this.findUsersEmployee(currentUser.id);

      const resignationData = {
        resignationType,
        effectiveDate,
        // separationDate: formData.separationDate || null,
        reason: formData.reason || "",
        status: "Pending",
        employee: { id: currentUser.employee.id },
        resignationLetter: resignationLetter || "",
        recruiter: { id: user?.recruiter?.id || null },
        coordinator: { id: user?.coordinator?.id || null },
      };

      const savedResignation = await this.upsertUseCase.execute(
        "resignation",
        resignationData
      );

      await this.fetchResignations();

      this.view.successModal("Please wait for the coordinator's approval");
      this.view.closeForm();

      return savedResignation;
    } catch (error) {
      const errorMessage =
        error.message || "Failed to submit resignation request";
      this.view.showMessage(errorMessage, true);
    } finally {
      this.view.setSubmitting(false);
    }
  }

  // Generate a hash specifically for status tracking
  generateStatusHash(data) {
    if (!data || !data.length) return "";

    return data
      .map((item) => `${item.id}:${item.status}:${item.updated}`)
      .join("|");
  }

  // Modified hash function for data comparison that prioritizes status
  generateDataHash(data) {
    if (!data || !data.length) return "";

    return data
      .map((item) => {
        // Extract the key fields we want to compare
        const keys = [
          "id",
          "status",
          "effectiveDate",
          "separationDate",
          "updated",
        ];
        return keys.map((key) => `${key}:${item[key]}`).join("|");
      })
      .join(",");
  }

  async fetchResignations(silentRefresh = false) {
    try {
      const collection = "resignation";

      // Only show loading indicator if not a silent refresh
      if (!silentRefresh) {
        this.view.setState({ progress: true });
      }

      const currentUser = this.view.getCurrentUser();
      if (!currentUser || !currentUser.employee || !currentUser.employee.id) {
        this.objects = [];
        this.view.setState({ objects: [], progress: false, count: 0 });
        return [];
      }

      const employeeId = currentUser.employee.id;
      const locationId = currentUser.location?.id;

      // Query resignations for the current user
      const where = { "employee.id": employeeId };
      if (locationId) {
        where["location.id"] = locationId;
      }

      // Add timestamp query parameter to avoid caching
      const timestamp = new Date().getTime();
      if (timestamp - this.lastFetchTime < 200 && silentRefresh) {
        // Throttle fetches that are too close together (reduced from 1000ms to 200ms)
        return this.objects;
      }
      this.lastFetchTime = timestamp;

      const objects = await this.findObjectUseCase.execute(
        collection,
        where,
        this.sort,
        this.skip,
        this.limit
      );

      const filteredObjects = objects.filter(
        (obj) => obj.employee && obj.employee.id === employeeId
      );

      if (filteredObjects.length > 0) {
        // Fetch employee details
        try {
          const employeeDetails = await this.findObjectUseCase.execute(
            "employees",
            { id: employeeId },
            {},
            0,
            1
          );

          const employeeData = employeeDetails.find(
            (emp) => String(emp.id) === String(employeeId)
          );

          // Fetch location details using the same pattern as employee details
          let locationData = null;
          if (locationId) {
            const locationDetails = await this.findObjectUseCase.execute(
              "locations",
              { id: locationId },
              {},
              0,
              1
            );

            locationData = locationDetails.find(
              (loc) => String(loc.id) === String(locationId)
            );
          }

          // Apply employee and location data to resignation objects
          filteredObjects.forEach((obj) => {
            // Enrich with employee data if available
            if (employeeData) {
              obj.employee = {
                ...obj.employee,
                ...employeeData,
              };
            }

            // Enrich with location data if available
            if (locationData) {
              obj.location = {
                ...obj.location,
                ...locationData,
              };
            }
          });
        } catch (error) {
          console.warn("Error enriching employee or location data:", error);
        }
      }

      // Generate hash of the new data
      const newDataHash = this.generateDataHash(filteredObjects);
      const newStatusHash = this.generateStatusHash(filteredObjects);

      // Check if either full data or just status has changed
      const hasDataChanged = newDataHash !== this.lastDataHash;
      const hasStatusChanged = newStatusHash !== this.lastStatusHash;

      // Update hash values
      this.lastDataHash = newDataHash;
      this.lastStatusHash = newStatusHash;

      // Update UI if anything changed or not a silent refresh
      if (hasDataChanged || hasStatusChanged || !silentRefresh) {
        this.objects = filteredObjects;

        // Update state with new data - prioritize status changes
        this.view.setState(
          {
            objects: filteredObjects,
            progress: false,
          },
          () => {
            // Only animate if not a silent refresh or if data has changed
            if (!silentRefresh || (hasDataChanged && !hasStatusChanged)) {
              setTimeout(
                () => this.view.setState({ animationActive: true }),
                100
              );
            }
          }
        );
      } else if (silentRefresh) {
        // If silent refresh and no changes, just update progress
        this.view.setState({ progress: false });
      }

      const count = await this.countObjectUseCase.execute(collection, where);
      this.view.setState({ count });

      return this.objects;
    } catch (error) {
      console.error("Error fetching resignations:", error);
      this.view.setState({ progress: false });

      // Only show error message if not a silent refresh
      if (!silentRefresh) {
        this.view.showMessage("Failed to load resignation requests", true);
      }
      return [];
    }
  }

  async updateSchema(schema) {
    try {
      const updatedSchema = await this.updateSchemaUseCase.execute(schema);

      // Update schemas in the view
      const schemas = this.view.getSchemas();
      const index = schemas.findIndex(
        (s) => s.collection === updatedSchema.collection
      );

      if (index !== -1) {
        schemas[index] = updatedSchema;
        this.view.setSchemas(schemas);
      }

      this.view.showMessage("Schema successfully updated");
    } catch (error) {
      this.view.showMessage("Failed to update schema", true);
    }
  }
}

export default EmpResignationPagePresenter;
